export interface DataFit {
  slope: number;
  intercept: number;
  cd: number;
  startX: number;
  endX: number;
}

const MIN_SAMPLES = 6;

function fitLine(xValues: number[], yValues: number[]): { intercept: number; slope: number } {
  let meanX = 0;
  let meanY = 0;
  for (let i = 0; i < xValues.length; i += 1) {
    meanX += xValues[i];
    meanY += yValues[i];
  }
  meanX /= xValues.length;
  meanY /= yValues.length;

  let covariance = 0;
  let variance = 0;
  for (let i = 0; i < xValues.length; i += 1) {
    const diff = xValues[i] - meanX;
    covariance += diff * (yValues[i] - meanY);
    variance += diff * diff;
  }

  const slope = covariance / variance;
  return {
    intercept: meanY - slope * meanX,
    slope,
  };
}

function rSquared(modelledValues: number[], observedValues: number[]): number {
  let n = 0;
  let r = 0;
  let meanA = 0;
  let meanB = 0;
  let varA = 0;
  let varB = 0;

  for (let i = 0; i < modelledValues.length; i += 1) {
    const deltaA = modelledValues[i] - meanA;
    n += 1;
    const scaleDeltaA = deltaA / n;
    const deltaB = observedValues[i] - meanB;
    const scaleDeltaB = deltaB / n;
    meanA += scaleDeltaA;
    meanB += scaleDeltaB;
    varA += scaleDeltaA * deltaA * (n - 1);
    varB += scaleDeltaB * deltaB * (n - 1);
    r += (deltaA * deltaB * (n - 1)) / n;
  }

  const correlation = r / Math.sqrt(varA * varB);
  return correlation * correlation;
}

export function dataFitting(
  xValues: number[] | null | undefined,
  yValues: number[] | null | undefined,
  threshold = 0.001,
): DataFit {
  let bestSlope = 0;
  let bestIntercept = 0;
  let bestStartX = 0;
  let bestEndX = 0;
  let bestCD = 0;

  const emptyFit = (): DataFit => ({
    slope: bestSlope,
    intercept: bestIntercept,
    cd: bestCD,
    startX: bestStartX,
    endX: bestEndX,
  });

  if (!xValues || !yValues || xValues.length !== yValues.length) return emptyFit();
  if (xValues.length < MIN_SAMPLES) return emptyFit();

  const ys = yValues.map((y) => Math.fround(y));
  const floatThreshold = Math.fround(threshold);
  const n = xValues.length;
  let bestInlierCount = 0;

  for (let i = 0; i < n - 1; i += 1) {
    for (let j = i + 1; j < n; j += 1) {
      const x1 = Math.min(xValues[i], xValues[j]);
      const x2 = Math.max(xValues[i], xValues[j]);
      if (x1 !== x2) {
        const xSeg: number[] = [];
        const ySeg: number[] = [];
        for (let k = 0; k < n; k += 1) {
          const x = xValues[k];
          if (x >= x1 && x <= x2) {
            xSeg.push(x);
            ySeg.push(ys[k]);
          }
        }

        if (xSeg.length >= MIN_SAMPLES) {
          const { intercept, slope } = fitLine(xSeg, ySeg);

          let inlierCount = 0;
          for (let k = 0; k < n; k += 1) {
            const yHat = slope * xValues[k] + intercept;
            if (Math.abs(ys[k] - yHat) < floatThreshold) inlierCount += 1;
          }

          const cd = rSquared(xSeg.map((x) => slope * x + intercept), ySeg);

          if (inlierCount > bestInlierCount || (inlierCount === bestInlierCount && cd > bestCD)) {
            bestInlierCount = inlierCount;
            bestSlope = Math.fround(slope);
            bestIntercept = Math.fround(intercept);
            bestCD = Math.fround(cd);
            bestStartX = Math.fround(xSeg[0]);
            bestEndX = Math.fround(xSeg[xSeg.length - 1]);
          }
        }
      }
    }
  }

  return {
    slope: bestSlope,
    intercept: bestIntercept,
    cd: 1,
    startX: bestStartX,
    endX: bestEndX,
  };
}
